/**
 * Monta os números de um mês: quanto cada representante faturou, quanto isso é
 * da meta, quanto é do faturamento da equipe e qual a projeção para o fim do
 * mês. As regras dos gráficos 1 (meta) e 2 (participação) moram aqui.
 *
 * Regras (gráficos da gerente + respostas dela em 12/10/2026):
 * - Vale o valor líquido (faturado − devolução).
 * - Arquivos por marca: o Winthor exporta um arquivo por marca. Se o mês tem
 *   arquivos de marcas ativas desta fonte, contam só eles (o arquivo geral fica
 *   guardado, mas não soma de novo). Arquivo de marca inativa nunca conta: assim
 *   a Laborene, que saiu do sistema, não entra no total, e um arquivo dela
 *   sozinho não esconde o arquivo geral do mês.
 * - Quem vendeu: o código do vendedor, no mês da venda (dominio/identificacao.ts).
 * - A meta de cada representante soma as marcas ativas cujo realizado vem desta
 *   fonte. No Winthor: Amend, Cora, Marco Boni e The Pro (a Bauny vem do SAP).
 * - Supermeta = meta + 5% (dominio/regras.ts).
 * - A participação é sobre o faturamento só dos representantes do ranking:
 *   o sócio e quem não tem meta ficam fora da conta (resposta 4).
 * - Ritmo e projeção contam dias úteis até a última data com faturamento.
 * - O mês de cada fonte é o seu "fechamento" (dominio/fechamento.ts): em geral o
 *   mês do calendário; Bauny 09/2026 vai até 01/10 (exceção combinada).
 * - Cada painel é de uma fonte: Aliança = marcas do Winthor; Bauny = SAP.
 * - Carteira atendida = clientes que compraram no mês ÷ clientes da carteira
 *   do representante (dominio/carteira.ts), quando há carteira importada.
 *
 * Lê: vendas, cadastros, metas e feriados que recebe (não busca nada sozinho).
 * Usado por: telas/Painel.tsx, telas/Representante.tsx e telas/Cadastros.tsx.
 */
import { atingimento, participacao, projetar } from './calculos.ts';
import { carteiraDoMes, clientesPorRepresentante } from './carteira.ts';
import { contarDiasUteisNoPeriodo } from './dias-uteis.ts';
import { mesDaVenda, periodoDoMes, type Periodo } from './fechamento.ts';
import { criarIdentificador, criarIdentificadorDeMarca, normalizarCodigo } from './identificacao.ts';
import { SUPERMETA_PERCENTUAL } from './regras.ts';
import type { Cadastros, Carteira, DataIso, Fonte, Marca, Meta, Mes, Representante, VendaParaContas } from './tipos.ts';
import { Acumulador, TOTAIS_ZERO, somarTotais, type Totais } from './totais.ts';

export type { Totais } from './totais.ts';

/** Meta, supermeta e quanto falta, para um valor realizado. */
export interface Desempenho {
  meta: number;
  /** Meta + 15%. */
  supermeta: number;
  atingimento: number | null;
  /** Quanto falta para bater a meta (0 se já bateu). */
  faltaParaMeta: number;
  projecao: number | null;
  atingimentoProjetado: number | null;
}

/** Quanto da carteira de clientes foi atendido no mês. */
export interface CarteiraAtendida {
  /** Clientes na carteira (só as situações que contam). null = nenhuma carteira importada. */
  clientesNaCarteira: number | null;
  /** Clientes que compraram no mês ÷ clientes na carteira. null = sem carteira (ou carteira vazia). */
  carteira: number | null;
}

export interface LinhaPainel extends Totais, Desempenho, CarteiraAtendida {
  representante: Representante;
  participacao: number | null;
}

export interface VendedorNaoIdentificado extends Totais {
  fonte: Fonte;
  codigo: string;
  nome: string;
}

/** De onde vieram as vendas do mês. */
export interface OrigemDasVendas {
  /** "por-marca" = arquivos de uma marca cada; "geral" = um arquivo com tudo junto. */
  modo: 'por-marca' | 'geral';
  /** Por marca: cada marca com arquivo e a última data de faturamento dela. */
  marcas: Array<{ marca: Marca; ultimaData: DataIso }>;
  /** Por marca: marcas com meta no mês que ainda não têm arquivo importado. */
  marcasSemArquivo: Marca[];
  /** Vendas guardadas que ficaram de fora: arquivo geral (quando há arquivos por marca) ou de marca inativa/de outra fonte. */
  vendasDeixadasDeFora: number;
}

export interface PainelMes {
  mes: Mes;
  fonte: Fonte;
  /** Primeiro e último dia que contam no mês desta fonte (fechamento; em geral, o mês do calendário). */
  periodo: Periodo;
  origem: OrigemDasVendas;
  /** Última data com faturamento no mês (null = mês sem vendas). */
  dataReferencia: DataIso | null;
  dias: { passados: number; total: number };
  /** Fração do mês que já passou, em dias úteis (regra I). null = mês sem vendas. */
  ritmo: number | null;
  marcasDaMeta: Marca[];
  /** Representantes do ranking que venderam ou tinham meta no mês (quem ainda não tinha entrado, ou já tinha saído, não aparece). */
  linhas: LinhaPainel[];
  /** Soma dos representantes do ranking: é a base da participação. */
  equipe: Totais & Desempenho & CarteiraAtendida;
  /** A foto da carteira usada no mês (null = nenhuma carteira importada desta fonte). */
  carteiraUsada: { dataReferencia: DataIso; clientesSemDono: number } | null;
  /** Quem vende mas fica fora do ranking e da conta (ex.: o sócio). */
  foraDoRanking: Array<Totais & { representante: Representante }>;
  /** Vendas de códigos que ainda não estão no cadastro (também ficam fora da conta). */
  naoIdentificados: VendedorNaoIdentificado[];
}

export interface EntradaPainel {
  mes: Mes;
  /** Fonte das vendas recebidas (todas as vendas precisam ser desta fonte). */
  fonte: Fonte;
  vendas: VendaParaContas[];
  cadastros: Cadastros;
  metas: Meta[];
  feriados: ReadonlySet<string>;
  /** Carteiras de clientes importadas (opcional: sem elas, o "% da carteira" fica vazio). */
  carteiras?: Carteira[];
}

/** Clientes da carteira e quanto dela foi atendido (null quando não há carteira). */
function atendida(clientes: number, clientesNaCarteira: number | null): CarteiraAtendida {
  return { clientesNaCarteira, carteira: clientesNaCarteira ? clientes / clientesNaCarteira : null };
}

/** Vendas que contam no mês desta fonte (o período do fechamento). */
export function vendasDoMes<T extends VendaParaContas>(vendas: T[], mes: Mes, fonte: Fonte): T[] {
  const { inicio, fim } = periodoDoMes(fonte, mes);
  return vendas.filter((venda) => venda.data >= inicio && venda.data <= fim);
}

/**
 * Escolhe quais vendas do mês contam (ver "Arquivos por marca" no topo).
 * Devolve as vendas que contam e a explicação de onde elas vieram.
 */
export function escolherVendas(entrada: EntradaPainel): { vendas: VendaParaContas[]; origem: OrigemDasVendas } {
  const doMes = vendasDoMes(entrada.vendas, entrada.mes, entrada.fonte);
  const porId = new Map(entrada.cadastros.marcas.map((marca) => [marca.id, marca]));
  // Vendas de arquivos de marca que contam: marca ativa, cujo realizado vem desta fonte.
  const contam = doMes.filter((venda) => {
    const marca = venda.marcaId == null ? undefined : porId.get(venda.marcaId);
    return marca !== undefined && marca.ativa && marca.fonte === entrada.fonte;
  });
  if (contam.length === 0) {
    // Nenhum arquivo de marca que conte: vale o arquivo geral do mês.
    const gerais = doMes.filter((venda) => venda.marcaId == null);
    return { vendas: gerais, origem: { modo: 'geral', marcas: [], marcasSemArquivo: [], vendasDeixadasDeFora: doMes.length - gerais.length } };
  }
  const ultimaData = new Map<number, DataIso>();
  for (const venda of contam) {
    const id = venda.marcaId as number;
    if ((ultimaData.get(id) ?? '') < venda.data) ultimaData.set(id, venda.data);
  }
  const comMeta = new Set(entrada.metas.filter((meta) => meta.mes === entrada.mes && meta.valor > 0).map((meta) => meta.marcaId));
  const marcasSemArquivo = entrada.cadastros.marcas
    .filter((marca) => marca.ativa && marca.fonte === entrada.fonte && comMeta.has(marca.id) && !ultimaData.has(marca.id))
    .sort((a, b) => a.ordem - b.ordem);
  const marcas = [...ultimaData.entries()]
    .map(([id, data]) => ({ marca: porId.get(id) as Marca, ultimaData: data }))
    .sort((a, b) => a.marca.ordem - b.marca.ordem);
  return { vendas: contam, origem: { modo: 'por-marca', marcas, marcasSemArquivo, vendasDeixadasDeFora: doMes.length - contam.length } };
}

/** Meta, supermeta, atingimento, falta e projeção para um realizado. */
export function calcularDesempenho(realizado: number, meta: number, dias: { passados: number; total: number }): Desempenho {
  const projecao = projetar(realizado, dias);
  return {
    meta,
    supermeta: meta * (1 + SUPERMETA_PERCENTUAL),
    atingimento: atingimento(realizado, meta),
    faltaParaMeta: Math.max(meta - realizado, 0),
    projecao,
    atingimentoProjetado: projecao === null ? null : atingimento(projecao, meta),
  };
}

export function montarPainel(entrada: EntradaPainel): PainelMes {
  const { mes, fonte, cadastros, metas, feriados } = entrada;
  const { vendas, origem } = escolherVendas(entrada);
  const identificar = criarIdentificador(cadastros.codigos);

  // 1. Marcas que entram na meta: as ativas cujo realizado vem desta fonte.
  //    (Para mostrar na tela, só as que têm meta no mês.)
  const idsMarcasDaMeta = new Set(cadastros.marcas.filter((marca) => marca.ativa && marca.fonte === fonte).map((marca) => marca.id));
  const idsComMetaNoMes = new Set(metas.filter((meta) => meta.mes === mes && meta.valor > 0).map((meta) => meta.marcaId));
  const marcasDaMeta = cadastros.marcas
    .filter((marca) => idsMarcasDaMeta.has(marca.id) && idsComMetaNoMes.has(marca.id))
    .sort((a, b) => a.ordem - b.ordem);

  // 2. Soma das vendas por representante (ou por código, quando não cadastrado).
  const porRepresentante = new Map<number, Acumulador>();
  const porCodigoDesconhecido = new Map<string, { nome: string; acumulador: Acumulador }>();
  for (const venda of vendas) {
    const id = identificar(fonte, venda.codigoVendedor, mesDaVenda(fonte, venda.data));
    if (id === null) {
      const codigo = normalizarCodigo(venda.codigoVendedor);
      const item = porCodigoDesconhecido.get(codigo) ?? { nome: venda.nomeVendedor, acumulador: new Acumulador() };
      item.acumulador.somar(venda);
      porCodigoDesconhecido.set(codigo, item);
    } else {
      const acumulador = porRepresentante.get(id) ?? new Acumulador();
      acumulador.somar(venda);
      porRepresentante.set(id, acumulador);
    }
  }

  // 3. Meta de cada representante no mês (só as marcas desta fonte).
  const metaPorRepresentante = new Map<number, number>();
  for (const meta of metas) {
    if (meta.mes !== mes || !idsMarcasDaMeta.has(meta.marcaId)) continue;
    metaPorRepresentante.set(meta.representanteId, (metaPorRepresentante.get(meta.representanteId) ?? 0) + meta.valor);
  }

  // 4. Data de referência e dias úteis (ritmo e projeção).
  const dataReferencia = vendas.reduce<DataIso | null>((maior, venda) => (maior === null || venda.data > maior ? venda.data : maior), null);
  const periodo = periodoDoMes(fonte, mes);
  const dias = contarDiasUteisNoPeriodo(periodo, dataReferencia, feriados);
  const ritmo = dataReferencia === null || dias.total === 0 ? null : dias.passados / dias.total;

  // 5. Separa quem está no ranking de quem fica fora da conta.
  const doRanking: Array<{ representante: Representante; totais: Totais; meta: number }> = [];
  const foraDoRanking: PainelMes['foraDoRanking'] = [];
  for (const representante of cadastros.representantes) {
    const totais = porRepresentante.get(representante.id)?.totais() ?? TOTAIS_ZERO;
    if (!representante.entraNoRanking) {
      if (totais.notas > 0) foraDoRanking.push({ representante, ...totais });
      continue;
    }
    const meta = metaPorRepresentante.get(representante.id) ?? 0;
    // Representantes mudam: cada mês mostra só quem vendeu ou tinha meta naquele mês.
    if (totais.notas === 0 && meta === 0) continue;
    doRanking.push({ representante, totais, meta });
  }

  // 6. Carteira de clientes do mês (a foto mais recente até o fim do mês), por representante.
  const carteira = entrada.carteiras ? carteiraDoMes(entrada.carteiras, fonte, mes) : null;
  const daCarteira = carteira ? clientesPorRepresentante(carteira.contagens, (codigo) => identificar(fonte, codigo, mes)) : null;
  const naCarteira = (id: number) => (daCarteira ? (daCarteira.porRepresentante.get(id) ?? 0) : null);

  // 7. Equipe = soma do ranking. É a base da participação.
  const totaisEquipe = somarTotais(doRanking.map((item) => item.totais));
  const metaEquipe = doRanking.reduce((soma, item) => soma + item.meta, 0);
  const linhas: LinhaPainel[] = doRanking.map(({ representante, totais, meta }) => ({
    representante,
    ...totais,
    ...calcularDesempenho(totais.liquido, meta, dias),
    ...atendida(totais.clientes, naCarteira(representante.id)),
    participacao: participacao(totais.liquido, totaisEquipe.liquido),
  }));
  const carteiraDaEquipe = daCarteira ? doRanking.reduce((soma, item) => soma + (naCarteira(item.representante.id) ?? 0), 0) : null;

  const naoIdentificados: VendedorNaoIdentificado[] = [...porCodigoDesconhecido.entries()]
    .map(([codigo, item]) => ({ fonte, codigo, nome: item.nome, ...item.acumulador.totais() }))
    .sort((a, b) => b.liquido - a.liquido);

  return {
    mes,
    fonte,
    periodo,
    origem,
    dataReferencia,
    dias,
    ritmo,
    marcasDaMeta,
    linhas,
    equipe: { ...totaisEquipe, ...calcularDesempenho(totaisEquipe.liquido, metaEquipe, dias), ...atendida(totaisEquipe.clientes, carteiraDaEquipe) },
    carteiraUsada: carteira && daCarteira ? { dataReferencia: carteira.dataReferencia, clientesSemDono: daCarteira.semDono } : null,
    foraDoRanking,
    naoIdentificados,
  };
}

export interface LinhaPorMarca extends Desempenho {
  marca: Marca;
  /** null quando ainda não há como saber o realizado desta marca. */
  realizado: number | null;
  /** Por que o realizado está vazio (para mostrar na tela). */
  motivoSemRealizado: string | null;
}

export interface DetalheRepresentante {
  linha: LinhaPainel | null;
  /** Vendas do representante no mês (as que contam), mais recentes primeiro. */
  vendas: VendaParaContas[];
  porMarca: LinhaPorMarca[];
  /** Vendas desta fonte que vieram sem marca (ou com marca desconhecida). */
  semMarca: number;
  /** true se as vendas do mês trazem a marca (arquivos por marca). */
  temMarcaNosDados: boolean;
}

/** Números de um representante: a linha do painel, as notas e a visão por marca (item 3). */
export function montarDetalheRepresentante(entrada: EntradaPainel, representanteId: number, painel: PainelMes): DetalheRepresentante {
  const identificar = criarIdentificador(entrada.cadastros.codigos);
  const identificarMarca = criarIdentificadorDeMarca(entrada.cadastros.marcas);
  // No SAP o arquivo é de uma marca só (Bauny): se o nome da coluna Marca não bater com o
  // cadastro (ex.: a marca foi renomeada), vale a única marca ativa que vem do SAP.
  const marcasDaFonte = entrada.cadastros.marcas.filter((m) => m.ativa && m.fonte === entrada.fonte);
  const marcaUnica = entrada.fonte === 'sap-bauny' && marcasDaFonte.length === 1 ? (marcasDaFonte[0]?.id ?? null) : null;
  const marcaDaVenda = (venda: VendaParaContas) => venda.marcaId ?? identificarMarca(venda.marcaOrigem) ?? marcaUnica;
  const vendas = escolherVendas(entrada)
    .vendas.filter((venda) => identificar(entrada.fonte, venda.codigoVendedor, mesDaVenda(entrada.fonte, venda.data)) === representanteId)
    .sort((a, b) => (a.data === b.data ? b.nf.localeCompare(a.nf, 'pt-BR', { numeric: true }) : b.data.localeCompare(a.data)));

  const temMarcaNosDados = painel.origem.modo === 'por-marca' || vendas.some((venda) => venda.marcaOrigem !== null);
  const realizadoPorMarca = new Map<number, number>();
  let semMarca = 0;
  for (const venda of vendas) {
    const marcaId = marcaDaVenda(venda);
    if (marcaId === null) semMarca += venda.valorLiquido;
    else realizadoPorMarca.set(marcaId, (realizadoPorMarca.get(marcaId) ?? 0) + venda.valorLiquido);
  }

  const metaPorMarca = new Map<number, number>();
  for (const meta of entrada.metas) {
    if (meta.mes === entrada.mes && meta.representanteId === representanteId) {
      metaPorMarca.set(meta.marcaId, (metaPorMarca.get(meta.marcaId) ?? 0) + meta.valor);
    }
  }
  const semArquivo = new Set(painel.origem.marcasSemArquivo.map((marca) => marca.id));

  // Só as marcas deste painel (desta fonte). Marca "a definir" aparece no painel do Winthor.
  const destePainel = (marca: Marca) => marca.fonte === entrada.fonte || (marca.fonte === null && entrada.fonte === 'winthor');
  const porMarca: LinhaPorMarca[] = entrada.cadastros.marcas
    .filter((marca) => marca.ativa && destePainel(marca) && ((metaPorMarca.get(marca.id) ?? 0) > 0 || realizadoPorMarca.has(marca.id)))
    .sort((a, b) => a.ordem - b.ordem)
    .map((marca) => {
      let motivoSemRealizado: string | null = null;
      if (marca.fonte === null) motivoSemRealizado = 'Ainda não sabemos de onde vem o faturamento desta marca.';
      else if (!temMarcaNosDados) motivoSemRealizado = 'O arquivo importado não traz a marca de cada venda.';
      else if (semArquivo.has(marca.id)) motivoSemRealizado = 'Ainda não foi importado o arquivo desta marca no mês.';
      const realizado = motivoSemRealizado === null ? (realizadoPorMarca.get(marca.id) ?? 0) : null;
      const desempenho = calcularDesempenho(realizado ?? 0, metaPorMarca.get(marca.id) ?? 0, painel.dias);
      return {
        marca,
        realizado,
        motivoSemRealizado,
        ...desempenho,
        ...(realizado === null ? { atingimento: null, projecao: null, atingimentoProjetado: null } : {}),
      };
    });

  return {
    linha: painel.linhas.find((linha) => linha.representante.id === representanteId) ?? null,
    vendas,
    porMarca,
    semMarca,
    temMarcaNosDados,
  };
}
